import { getShortenedClass, Pair, TimeService } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { LoggingService } from '@adaptiva/core/logging';
import { INotificationEvent, INotificationService, NotificationRegistration } from '@adaptiva/core/notifications';
import { BrandingService, EJavaClassNames, ENotificationTypes, ObjectManagerService } from '@adaptiva/data-service';
import { HttpParams } from '@angular/common/http';
import { computed, effect, EffectRef, inject, Injectable, linkedSignal, Signal, signal, WritableSignal } from '@angular/core';
import { combineLatest, EMPTY, map, Observable, Subscription, switchMap, tap, throwError, timer } from 'rxjs';
import { LlmConversation, LlmConversationNode } from '../../classes';
import { ELlmNotifications } from '../../constants';
import { ELlmTabs, ELlmViews } from '../../enums';
import { ILlmConversation, ILlmConversationNode, ILlmDashboardPayload, ILlmDashboardSaveRequest, ILlmRequest, IRenameConversationRequest } from '../../interfaces';
import { AIDA_DISPLAY_NAME, AIDA_OEM_COMPATIBILITY_LIST } from '../../tokens';
import { LlmClient } from '../llm-client';

@Injectable()
export class AidaService extends LlmClient {

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly logger: LoggingService = inject(LoggingService);

	protected readonly notifications: INotificationService = inject(INotificationService);

	protected readonly objectManager: ObjectManagerService = inject(ObjectManagerService);

	protected readonly branding: BrandingService = inject(BrandingService);

	protected readonly oemCompatibilityList: string[] = inject(AIDA_OEM_COMPATIBILITY_LIST);

	public readonly conversation: WritableSignal<ILlmConversation> = signal(new LlmConversation());

	public readonly conversationTree: WritableSignal<LlmConversationNode | null> = signal(null);

	public readonly currentTab: WritableSignal<ELlmTabs> = signal(ELlmTabs.CHAT);

	public readonly displayName: WritableSignal<string> = inject(AIDA_DISPLAY_NAME);

	public readonly isOpen: WritableSignal<boolean> = signal(false);

	public readonly view: WritableSignal<ELlmViews> = signal(ELlmViews.CHAT);

	public readonly isEnabled: Signal<boolean> = computed(() => {
		const _oem: string = this.branding.oem();
		const _compat: string[] = this.oemCompatibilityList;

		return _compat.find(x => x.toLowerCase().trim() === _oem.toLowerCase().trim()) != null;
	});

	public readonly conversationId: Signal<long> = computed(() => {
		return this.conversation()?.objectId;
	});

	public readonly isSearchOpen: WritableSignal<boolean> = linkedSignal(() => {
		const _tab: ELlmTabs = this.currentTab();
		const _isOpen: boolean = this.searchToggles.get(_tab)();

		return _isOpen;
	});

	public readonly isChatView: Signal<boolean> = computed(() => {
		return this.view() === ELlmViews.CHAT;
	});

	protected readonly heartbeatInterval: WritableSignal<int> = signal(TimeService.oneMinute * 5);

	protected heartbeatSubscription!: Subscription;

	protected notificationSubscription!: Subscription;

	protected readonly searchToggles: Map<ELlmTabs, WritableSignal<boolean>> = new Map([
		[ELlmTabs.CHAT, signal(false)],
		[ELlmTabs.FORKS, signal(false)],
		[ELlmTabs.HISTORY, signal(false)],
	]);

	protected readonly changeConversationFx: EffectRef = effect(() => {
		const _conversationId: long = this.conversationId();

		this._manageNotifications(_conversationId);
		this.manageHeartBeat();

		if (!_conversationId) {
			this.conversationTree.set(null);

			return;
		}

		this.openConversationTree({ conversationId: _conversationId }).pipe(
			this.takeOneUntilDestroyed()
		).subscribe({
			next: (_tree: LlmConversationNode) => {
				this.conversationTree.set(_tree);
			},
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'AidaService.changeConversationFx');
				this.conversationTree.set(null);
			}
		});
	});

	protected readonly nodeMap: Signal<Map<string, LlmConversationNode>> = computed(() => {
		return this._createNodeMap(this.conversationTree());
	});

	/**
	 * Retrives a `LlmConversation` and sets the `AidaService.conversation`
	 * @param _conversationId Desired conversation
	 * @returns Fetched `LlmConversation`
	 */
	public startConversation(): Observable<ILlmConversation>;
	public startConversation(conversationId: long): Observable<ILlmConversation>;
	public startConversation(_conversationId?: long): Observable<ILlmConversation> {
		if (!_conversationId) {
			this.setConversation();

			return EMPTY;
		}

		return this.objectManager.getObjectById(EJavaClassNames.LlmConversation, _conversationId).pipe(
			this.takeOneUntilDestroyed(),
			tap((_conversation: ILlmConversation) => {
				this.setConversation(_conversation);
			})
		);
	}

	/** Create a new LlmConversation
	 * @param _message The message that should be used to create the root of the conversation
	 * @returns The root node of the new conversation tree
	 */
	public createConversation(_message: string): Observable<void> {
		const _url: string = `${this.getBaseUrl()}/new-conversation`;

		if (!_message)
			return throwError(() => new Error('LLM Query is empty'));

		return this.post(_url, null, { params: new HttpParams().set('message', _message) }).pipe(
			this.takeOneUntilDestroyed(),
			map((_conversation: ILlmConversation) => {
				this.setConversation(_conversation);
			})
		);
	}

	/** Set the `AidaService` conversation
	 * @param conversation The conversation to set. If not provided, a blank class instance will be set
	 */
	public setConversation(conversation: ILlmConversation): void;
	public setConversation(): void;
	public setConversation(_conversation?: ILlmConversation): void {
		this.conversation.set(_conversation || new LlmConversation());
	}

	/** Get the root node of a conversation
	 * @param _request Provides the `LlmConversation.objectId` of the desired conversation
	 */
	public openConversationTree(_request: ILlmRequest): Observable<LlmConversationNode> {
		const _url: string = `${this.getBaseUrl()}/open-conversation`;

		if (!_request == null)
			return throwError(() => new Error('No request provided'));

		return this.put(_url, null, { params: new HttpParams().set('conversationId', _request.conversationId) }).pipe(
			map((_node: ILlmConversationNode) => new LlmConversationNode(_node))
		);
	}

	public toggleSearch(): void {
		const _tab: ELlmTabs = this.currentTab();
		const _isSearchOpen: WritableSignal<boolean> = this.searchToggles.get(_tab);

		_isSearchOpen.update((_isOpen: boolean) => !_isOpen);
	}

	/**
	 * Rename a `LlmConversation`
	 * @param _request Defines the target conversation and desired updated name
	 * @returns Success/Failure
	 */
	public renameConversation(_request: IRenameConversationRequest): Observable<boolean> {
		const _url: string = `${this.getBaseUrl()}/rename-conversation`;

		if (!_request == null)
			return throwError(() => new Error('No request provided'));

		return this.put(_url, _request);
	}

	public saveDashboard(_dashboardName: string, _nodeId: string): Observable<int> {
		const _url: string = `${this.getBaseUrl()}/save-dashboard`;

		const _request: ILlmDashboardSaveRequest = {
			dashboardName: _dashboardName,
			conversationId: this.conversationId(),
			nodeId: _nodeId,
		};

		return this.post(_url, _request);
	}

	/**
	 * Regenerate the dashboard payload for a given node
	 * @param _nodeId 
	 * @returns An updated Dashboard payload
	 */
	public regenerateNode(_nodeId: string): Observable<ILlmDashboardPayload> {
		const _url: string = `${this.getBaseUrl()}/regenerate-node`;

		if (!_nodeId == null)
			return throwError(() => new Error('No request provided'));

		const _conversationId: long = this.conversation()?.objectId;

		if (!_conversationId)
			return throwError(() => new Error('No active conversation'));

		return this.put(_url, { nodeId: _nodeId, conversationId: _conversationId });
	}

	public addConversationNode(_parentNodeId: string, _node: ILlmConversationNode): LlmConversationNode {
		const _nodeMap: Map<string, LlmConversationNode> = this.nodeMap();

		if (_nodeMap.has(_node.nodeId))
			return null;

		if (!_nodeMap.has(_parentNodeId)) {
			this.logger.warn('AidaService.addConversationNode', 'Failed to add Conversation Node. Parent node not found.')();

			return null;
		}

		const _newNode: LlmConversationNode = new LlmConversationNode(_node);

		_nodeMap.get(_parentNodeId).children.push(_newNode);
		this.conversationTree.update(t => new LlmConversationNode(t));

		return _newNode;
	}

	public removeConversationNode(_targetNodeId: string): void {
		const _nodeMap: Map<string, LlmConversationNode> = this.nodeMap();

		if (!_nodeMap.has(_targetNodeId)) {
			this.logger.warn('AidaService.removeConversationNode', 'Failed to remove Conversation Node. Node not found.')();

			return;
		}

		const _parentNode: LlmConversationNode = _nodeMap.get(_targetNodeId)?.parent;

		if (_parentNode)
			_parentNode.children = _parentNode.children.filter(c => c.nodeId !== _targetNodeId);

		this.conversationTree.update(t => new LlmConversationNode(t));
	}

	public getConversationNode(_targetNodeId: string): LlmConversationNode | null {
		const _nodeMap: Map<string, LlmConversationNode> = this.nodeMap();

		if (_nodeMap.has(_targetNodeId))
			return _nodeMap.get(_targetNodeId);

		return null;
	}

	protected manageHeartBeat(): void {
		this.heartbeatSubscription?.unsubscribe();

		const _conversationId: long = this.conversationId();

		if (!_conversationId)
			return;

		this.heartbeatSubscription = timer(this.heartbeatInterval()).pipe(
			switchMap(() => this.sendHeartbeat({ conversationId: _conversationId }))
		).subscribe({
			next: () => this.manageHeartBeat(),
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'AidaService.manageHeartbeat');
			}
		});
	}

	/**
	 * Refresh the conversation's timer on the server
	 */
	protected sendHeartbeat(_request: ILlmRequest): Observable<void> {
		const _url: string = `${this.getBaseUrl()}/conversation-heartbeat`;

		if (!_request)
			return throwError(() => new Error('No request provided'));

		return this.put(_url, null, { params: new HttpParams().set('conversationId', _request.conversationId) }).pipe(
			this.takeOneUntilDestroyed(),
		);
	}

	private _manageNotifications(_conversationId: long): void {
		this.notificationSubscription?.unsubscribe();

		if (!_conversationId)
			return;

		const _id: string = _conversationId.toString();
		const _class: string = getShortenedClass(EJavaClassNames.LlmConversation);
		const _notifications: Observable<INotificationEvent>[] = [
			this.notifications.listen(new NotificationRegistration(`${_class}${ENotificationTypes.AFTER_OBJECT_MODIFIED}`, _id)).pipe(
				tap((_notification: INotificationEvent<[ILlmConversation, ILlmConversation]>) => {
					const [_previous, _next]: [ILlmConversation, ILlmConversation] = _notification.value;

					this.conversation.update((_conversation: ILlmConversation) => {
						return new LlmConversation(_next);
					});
				})
			),
			this.notifications.listen(new NotificationRegistration(`${_class}${ENotificationTypes.AFTER_OBJECT_DELETED}`, _id)).pipe(
				tap((_notification: INotificationEvent) => this.setConversation())
			),
			this.notifications.listen(new NotificationRegistration(`${_class}${ELlmNotifications.NODE_ADDED}`, _id)).pipe(
				tap((_notification: INotificationEvent<Pair<string, ILlmConversationNode>>) => {
					this.addConversationNode(_notification.value.left, _notification.value.right);
				})
			),
			this.notifications.listen(new NotificationRegistration(`${_class}${ELlmNotifications.NODE_DELETED}`, _id)).pipe(
				tap((_notification: INotificationEvent<string>) => this.removeConversationNode(_notification.value))
			)
		];

		this.notificationSubscription?.unsubscribe();
		this.notificationSubscription = combineLatest(_notifications).pipe(
			this.takeUntilDestroyed()
		).subscribe({
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'AidaService._manageNotifications');
			}
		});
	}

	private _createNodeMap(node: LlmConversationNode): Map<string, LlmConversationNode>
	private _createNodeMap(node: LlmConversationNode, nodeMap: Map<string, LlmConversationNode>): Map<string, LlmConversationNode>
	private _createNodeMap(
		_node: LlmConversationNode,
		_nodeMap: Map<string, LlmConversationNode> = new Map()
	): Map<string, LlmConversationNode> {
		if (_node == null)
			return _nodeMap;

		for (const _child of _node.children)
			this._createNodeMap(_child, _nodeMap);

		return _nodeMap.set(_node.nodeId, _node);
	}

}
